import React from "react";
import { createPortal } from "react-dom";
import { Box, Button, Chip, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { ArrowLeft, Save, Sparkles } from "lucide-react";
import { projectedPageFontSx, projectedSaveButtonSx } from "./projectedRetreatUi";
import type { useProjectedActionBarPin } from "./useProjectedActionBarPin";
import type { ProjectedRetreatFormModel } from "./useProjectedRetreatForm";

type FormModel = ProjectedRetreatFormModel;
type PinModel = ReturnType<typeof useProjectedActionBarPin>;

export function ProjectedRetreatActionBar({
  form,
  pin,
}: {
  form: FormModel;
  pin: PinModel;
}) {
  const {
    navigate,
    saving,
    dirty,
    handleSave,
    displayPercent,
    saveStatusLabel,
    saveStatusShort,
    transitionSuccess,
  } = form;
  const {
    actionBarSentinelRef,
    actionBarRef,
    actionBarPinned,
    actionBarPin,
  } = pin;

  const bar = (
    <Paper
      ref={actionBarRef}
      component="nav"
      elevation={0}
      sx={{
        ...projectedPageFontSx,
        ...(actionBarPinned
          ? {
              position: "fixed",
              top: actionBarPin.top,
              left: actionBarPin.left,
              width: actionBarPin.width,
              mb: 0,
              boxShadow:
                "0 12px 32px -4px rgba(15, 23, 42, 0.12), 0 4px 12px -2px rgba(15, 23, 42, 0.06)",
            }
          : {
              position: "relative",
              mb: 2.5,
              boxShadow: "0 2px 10px rgba(15, 23, 42, 0.04)",
            }),
        zIndex: (t) => t.zIndex.modal,
        px: { xs: 1.5, sm: 2.5 },
        py: 1.25,
        borderRadius: 3,
        bgcolor: "rgba(255, 255, 255, 0.92)",
        backdropFilter: "blur(16px)",
        border: "1px solid #e2e8f0",
        transition: "box-shadow 0.2s ease, background-color 0.2s ease",
      }}
      aria-label="Projected Retreat page actions"
    >
      <Stack
        direction="row"
        spacing={{ xs: 1, sm: 2 }}
        alignItems="center"
        justifyContent="space-between"
      >
        <Button
          size="small"
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate("/manageRetreat")}
          sx={{
            textTransform: "none",
            flexShrink: 0,
            fontWeight: 600,
            color: "#475569",
            borderRadius: 2,
            px: 1.5,
            "&:hover": {
              bgcolor: "#f1f5f9",
              color: "#0f172a",
            },
          }}
        >
          Retreats Dashboard
        </Button>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ minWidth: 0, px: 1 }}
        >
          <Chip
            size="small"
            icon={<Sparkles size={13} color="#2563eb" />}
            label="Projected Stage"
            sx={{
              display: { xs: "none", md: "inline-flex" },
              bgcolor: "#eff6ff",
              color: "#1e40af",
              fontWeight: 700,
              fontSize: "0.75rem",
              borderRadius: 2,
              border: "1px solid #bfdbfe",
            }}
          />

          <Box sx={{ textAlign: "center", minWidth: 120 }}>
            <Typography
              variant="body2"
              aria-live="polite"
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
                color: dirty ? "#d97706" : "#059669",
                fontSize: "0.85rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: dirty ? "#f59e0b" : "#10b981",
                  boxShadow: dirty
                    ? "0 0 0 3px rgba(245, 158, 11, 0.2)"
                    : "0 0 0 3px rgba(16, 185, 129, 0.2)",
                }}
              />
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                {saveStatusLabel}
              </Box>
              <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                {saveStatusShort}
              </Box>
            </Typography>
          </Box>

          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              gap: 1.25,
              bgcolor: "#f8fafc",
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              border: "1px solid #e2e8f0",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "#64748b", whiteSpace: "nowrap" }}
            >
              Planning {displayPercent}%
            </Typography>
            <Box sx={{ width: 64 }}>
              <LinearProgress
                variant="determinate"
                value={displayPercent}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: "#e2e8f0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    background: "linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)",
                  },
                }}
              />
            </Box>
          </Box>
        </Stack>

        <Button
          size="small"
          variant="contained"
          startIcon={<Save size={16} />}
          disabled={saving || Boolean(transitionSuccess)}
          onClick={handleSave}
          aria-label="Save from top action bar"
          sx={{
            ...projectedSaveButtonSx,
            flexShrink: 0,
            borderRadius: 2.5,
            px: { xs: 2, sm: 2.5 },
            py: 0.85,
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            {saving ? "Saving…" : "Save Projected Retreat"}
          </Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
            {saving ? "…" : "Save"}
          </Box>
        </Button>
      </Stack>
    </Paper>
  );

  return (
    <>
      <Box ref={actionBarSentinelRef} sx={{ height: 0 }} aria-hidden />
      {actionBarPinned ? (
        <Box sx={{ height: actionBarPin.height, mb: 2.5 }} aria-hidden />
      ) : null}
      {/* Portal when pinned so SingLayout overflow-x:hidden cannot trap fixed. */}
      {actionBarPinned && typeof document !== "undefined"
        ? createPortal(bar, document.body)
        : bar}
    </>
  );
}
